import React, { Children, forwardRef, memo, ReactNode, useCallback, useMemo, useRef } from 'react';
import { mergeRefs, useIsomorphicLayoutEffect, useResizeListener } from '@direct-frontend/stdlib';
import { DOMProps } from '@react-types/shared';
import ReactDOM from 'react-dom';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';

import { TestProps } from '../../../internal/types/testTypes';
import { cn } from '../../../lib/css/className/className';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';
import { HStack } from '../../Stack';
import { BaseFilterChip, FilterCommonProps } from '../BaseFilterChip';
import { FilterChip } from '../FilterChip';
import { FilterChipContext } from '../FilterChipContext/FilterChipContext';
import { skinMap } from '../BaseFilterChip/BaseFilterChip';

import './FilterItem.css';

export const cls = cn('FilterItem');

export interface FilterItemProps extends FilterCommonProps, Loggable, TestProps, DOMProps {
    /**
     * Чипса-ключ фильтра
     */
    keyChip: ReactNode;

    /**
     * Чипсы-значения фильтра
     * @expectedNode `FilterChip`
     */
    children?: ReactNode;

    /**
     * Колбэк нажатия на чип с крестиком. Крестик появляется при указании этого свойства
     */
    onRemove?: () => void;

    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Флаг автивности фильтра
     */
    isDisabled?: boolean;

    /**
     * Позволяет показать крестик удаления даже у readonly (isDisabled) фильтра.
     * По умолчанию у isDisabled фильтра крестик скрыт.
     */
    isRemovable?: boolean;

    /**
     * Ширина фильтра.
     * max — на всю ширину контейнера (без inline-раскладки).
     * @default auto
     */
    width?: 'auto' | 'max';
}

/**
 * Компонент для отображения отдельного фильтра и его значений
 */
export const FilterItem = memo(
    forwardRef<HTMLDivElement, FilterItemProps>(function FilterItem(
        {
            keyChip,
            size,
            color,
            children,
            onRemove,
            className,
            'data-testid': dataTestId,
            logNode,
            isDisabled,
            isRemovable,
            width = 'auto',
            ...props
        },
        forwardedRef,
    ) {
        const { LogProvider, logClick, logTech } = useLogger('FilterItem', logNode);
        const numberOfLines = useRef(0);

        const logNumberOfLines = useCallback(
            (newNumberOfLines: number) => {
                if (newNumberOfLines !== numberOfLines.current) {
                    logTech('wrapToNewLine', {
                        lines: newNumberOfLines,
                    });
                    numberOfLines.current = newNumberOfLines;
                }
            },
            [logTech],
        );

        const onRemoveHandler = useCallback(() => {
            onRemove?.();
            logClick({
                type: 'removeFilterOptions',
            });
        }, [onRemove, logClick]);

        const keyContext = useMemo(
            () => ({ size, color, role: 'key' as const, width: width === 'max' ? ('max' as const) : undefined }),
            [size, color, width],
        );
        const valueContext = useMemo(() => ({ size, color, role: 'value' as const }), [size, color]);
        const ref = useRef<HTMLElement>(null);
        const currentFillers = useRef<Element[]>([]);
        const childrenAmount = Children.count(children);

        // Данная функция обеспечивает заполнение пустых мест в конце строк внутри FilterItem
        const handleLastRowElements = useCallback(() => {
            const container = ref.current;

            if (container && container.hasChildNodes()) {
                for (const elementToRemove of currentFillers.current) {
                    elementToRemove.remove();
                }
                const childElements = Array.from(container.children);

                let lastTopPosition: number | null = null;
                // Шрифты влияют на ширину элемента, поэтому нам необходимо ждать их загрузки.
                // Промис резолвится раньше рендера, поэтому фликеринга не происходит
                if (document.fonts) {
                    document.fonts.ready
                        .then(() => {
                            // Получаем размеры контейнера ПОСЛЕ загрузки шрифтов
                            const containerBoundingRect = container.getBoundingClientRect();
                            const lastElements: Element[] = [];
                            function addToLastElements(candidate?: Element) {
                                if (
                                    candidate &&
                                    container &&
                                    // Мы не хотим создавать новый filler элемент,
                                    // если у нас нет места, чтобы его заполнить,
                                    // в ином случае мы будем видеть двойной border, при ширине filler в 0
                                    // а округляем ради firefox, потому что он особенный
                                    Math.abs(
                                        Math.floor(containerBoundingRect.right) -
                                            Math.ceil(candidate.getBoundingClientRect().right),
                                    ) > 1
                                ) {
                                    lastElements.push(candidate);
                                }
                            }
                            // Основная логика происходит здесь:
                            // Проходим по всем элементам, и смотрим, что верхняя граница элемента
                            // имеет ту же координату, что и верхняя граница предыдущего элемента,
                            // иначе мы увидели переход на след строку и надо добавить предыдущий элемент в список,
                            // который представляет каждый последний элемент для каждой строки
                            childElements.forEach((child, index) => {
                                const topPosition = child.getBoundingClientRect().top;
                                const lastElem = childElements[index - 1];

                                if (lastTopPosition !== null && topPosition !== lastTopPosition) {
                                    addToLastElements(lastElem);
                                }
                                lastTopPosition = topPosition;
                            });

                            addToLastElements(childElements[childElements.length - 1]);

                            lastElements.forEach(elem => {
                                const filler = document.createElement('div');
                                // eslint-disable-next-line react/no-deprecated -- legacy-код, замена — в задачах миграции на React 19 (DIRECT-341369)
                                ReactDOM.render(
                                    <FilterChipContext.Provider value={valueContext}>
                                        <FilterChip isFiller />
                                    </FilterChipContext.Provider>,
                                    filler,
                                );
                                currentFillers.current.push(...filler.children);
                                elem.after(...filler.childNodes);
                            });

                            logNumberOfLines(Math.max(lastElements.length, 1));
                        })
                        .catch(() => {});
                }
            }
        }, [valueContext, logNumberOfLines]);

        useIsomorphicLayoutEffect(() => {
            if (width !== 'max') {
                handleLastRowElements();
            }
        }, [handleLastRowElements, childrenAmount, size, color, keyChip, width]);

        const resizeRef = useResizeListener(handleLastRowElements);

        return (
            <LogProvider>
                <HStack
                    ref={forwardedRef}
                    className={cls({ size, color, width }, [getColorSkin(skinMap[color], {}), className])}
                    data-testid={dataTestId}
                    gap={color === 'contour' ? undefined : '2'}
                    inline
                    {...props}
                >
                    <HStack
                        ref={mergeRefs(ref, resizeRef)}
                        className={cls('content', { color, size })}
                        gap={color === 'contour' ? undefined : '2'}
                        flexWrap="wrap"
                        {...props}
                    >
                        <FilterChipContext.Provider value={keyContext}>{keyChip}</FilterChipContext.Provider>
                        <FilterChipContext.Provider value={valueContext}>{children}</FilterChipContext.Provider>
                    </HStack>
                    {onRemove && (isRemovable ?? !isDisabled) && (
                        <BaseFilterChip
                            className={cls('remove')}
                            size={size}
                            color={color}
                            role="system"
                            onClick={onRemoveHandler}
                            onRemove={onRemoveHandler}
                            data-testid={dataTestId && `${dataTestId}.Remove`}
                        />
                    )}
                </HStack>
            </LogProvider>
        );
    }),
);
